/**
 * Guided demo-tour step list (Prosser's sales walkthrough).
 *
 * Each step names a portal route to land on and an optional CSS selector to
 * spotlight. Selector `''` = a whole-page intro card (no cutout). Most selectors
 * anchor on stable wrapper elements that always render on their route
 * (`[data-tour="action-center"]` on the dashboard's pinned Action Center rail;
 * `[data-tour="driver-roster"]` on the drivers-view root). Two anchors sit on
 * customizable-grid widgets instead — `[data-tour="applicants"]` and
 * `[data-tour="handled-for-you"]` (positions 2 and 3 in the default layout, but a
 * client can remove either) — DemoTour degrades to a centered card if a selector
 * ever misses, so the tour never breaks.
 *
 * ORDER: the dashboard gets its multi-step set first, then every sidebar tab
 * top-to-bottom, none skipped (Dugger, 2026-07-24) — Fleet & Compliance
 * (Safety Scores → Accidents → Roadside Inspections → Drivers → Vehicles →
 * Training), then Account (Billing → Documents → Notifications).
 *
 * Plan: knowledge/portal/plans/2026-07-16-portal-demo-mode.md
 */

export interface DemoTourStep {
  route: string // portal route to navigate to before highlighting
  selector: string // CSS selector to spotlight; '' = whole-page intro card
  title: string
  body: string
  // NOTE: there is no per-step dim/placement flag. Every selector-less step (ten
  // of the fourteen below) renders undimmed with the card parked top-right —
  // DemoTour handles it uniformly, because Dugger's complaint applied to all of
  // them, not just the intro. See DemoTour.vue's header for what was rejected.
  //
  // Optional call-to-action rendered as a button on the step card — opens in a
  // NEW TAB (full-screen decks present better than an iframe modal, and the
  // demoer can flip back to the tour). Used by the Vehicles step to launch the
  // fleet-program presentation shipped in public/ (see that step's comment).
  cta?: { label: string; href: string }
  // Optional "See more" drill-in, IN-APP and opt-in (Dugger, 2026-07-31: "an
  // option on safety scores that is like see more and it shows more details,
  // same for the other steps"). Deliberately a button on the card rather than
  // extra linear steps — the tour stays 14 steps and the demoer decides per
  // prospect how deep to go.
  //
  // Exactly one of `route` (in-app navigation) or `click` (a selector on the
  // current page, e.g. a table row that opens a drawer) — declarative on
  // purpose so this module stays pure data with no router/closure imports.
  //
  // Leaving the step closes it again: a `route` drill-in is undone by the next
  // step's own navigation; a `click` drill-in by an Escape keydown, which every
  // PrimeVue drawer/dialog honours. `close` overrides that with a selector to
  // click instead — needed for hand-rolled overlays that DON'T listen for
  // Escape (the Team-training detail modal is one; it closes only via its own
  // buttons or a backdrop click).
  //
  // If the `click` target isn't in the DOM (empty register, a client with no
  // rows) the button simply does nothing rather than throwing — same
  // graceful-degradation contract as a missing spotlight anchor.
  seeMore?: { label: string; route?: string; click?: string; close?: string }
}

export const DEMO_TOUR_STEPS: DemoTourStep[] = [
  {
    route: '/',
    selector: '',
    title: 'Your compliance home base',
    body: 'Everything DOT wants from you, in one place — and most of it is handled for you before you ever see it.',
  },
  {
    route: '/',
    selector: '[data-tour="action-center"]',
    title: 'The Action Center',
    body: 'The short list of things that actually need you. Each one explains itself in plain English — no FMCSA jargon — and most resolve in one click.',
  },
  {
    route: '/',
    selector: '[data-tour="handled-for-you"]',
    title: 'Handled for you',
    body: 'Right on this dashboard you also see the work your consultant already did for you — filed documents, ordered reports — with the paper trail attached. That is why the Action Center stays short.',
  },
  {
    route: '/',
    selector: '[data-tour="applicants"]',
    title: 'Your hiring pipeline',
    body: 'Every applicant at a glance — and exactly where each one stands in screening. When someone clears every check and is ready to hire, they surface here on their own, so nothing stalls waiting on you.',
  },
  {
    route: '/safety-scores',
    selector: '',
    title: 'Your safety scores',
    body: 'Your live FMCSA standing, decoded. See what is driving each score and what to fix first.',
    // CSA Insights is an in-app route, not a new tab — it carries the projection
    // stepper and the what-if simulator. NAMING (legal): CAB's "BASICs
    // Calculator" is a registered mark, so this label must never use it — see
    // CSAInsightsView.vue's header note.
    seeMore: { label: 'See what drives each score', route: '/csa-insights' },
  },
  {
    route: '/accidents',
    selector: '',
    title: 'Accident registry',
    body: 'The DOT-required accident register, kept for you. Log one in under a minute — we will show you.',
    // Opens the first register row's detail drawer, where the driver assignment
    // and the crash-report upload live (Dugger, 2026-07-31: show "how they can
    // add a driver to an accident and see details and can upload the crash
    // report and how it keeps them compliant with the accident registry").
    // `.accident-row` is already on every row — no view change needed.
    seeMore: { label: 'Open an accident record', click: '.accident-row' },
  },
  {
    route: '/roadside-inspections',
    selector: '',
    title: 'Roadside inspections',
    body: 'Every roadside inspection on your record, pulled in automatically — what the officer found and what it means for your scores. When violations need the Section 396.9 correction certification, you sign it right here, inside the 15-day window.',
    // Opens the real §396.9(d) certification dialog on the first inspection that
    // still needs one. The Certify button is normally client-only; it renders
    // under demo mode by design — see RoadsideInspectionsView's comment and the
    // matching demo handler, which answers the POST locally so nothing is signed.
    // PrimeVue Dialog closes on Escape, so no `close` selector is needed.
    seeMore: { label: 'Show the sign-off', click: '[data-testid^="certify-"]' },
  },
  {
    route: '/drivers',
    selector: '[data-tour="driver-roster"]',
    title: 'Your drivers',
    body: 'Every driver, their qualification status, and what is coming due — CDLs, medical cards, annual reviews — tracked automatically.',
  },
  {
    route: '/vehicles',
    selector: '',
    title: 'Your fleet, tracked like your drivers',
    body: 'Registrations, insurance, annual inspections, IFTA — every vehicle’s paperwork status at a glance. And the full maintenance program is coming soon: a QR sticker on every truck that pulls up its docs at roadside, digital driver inspection reports, and maintenance logs that print straight into DOT-ready reports.',
    // The deck is the repo's knowledge/presentations/fleet-demo-presentation.html,
    // shipped verbatim as public/fleet-demo.html (Vite copies public/ to dist, so
    // nginx serves it unauthenticated — sales material, deliberate).
    cta: { label: 'Open the fleet program demo', href: '/fleet-demo.html' },
  },
  {
    route: '/training',
    selector: '',
    title: 'Driver training',
    body: 'Assigned courses, completions, and certificates — all tracked, all audit-ready. The team grid shows every driver month by month, so you can see who is behind and nudge them without chasing anyone down.',
    // Opens the per-driver / per-month detail modal from the team grid: course,
    // status, year-to-date completion, and a Send reminder action — the clearest
    // single view of what the page actually does. That modal is hand-rolled and
    // does NOT close on Escape, hence the explicit `close` selector.
    seeMore: {
      label: 'Open a training record',
      click: '[data-testid="training-cell"]',
      close: '[data-testid="training-detail-close"]',
    },
  },
  {
    route: '/billing',
    selector: '',
    title: 'Billing without surprises',
    body: 'Your subscription, exactly what services you are paying for, your next charge, and every invoice as a PDF. Nothing hidden.',
  },
  {
    route: '/documents',
    selector: '',
    title: 'Documents',
    body: 'Every filed document, one search away. When an auditor asks, you are done in minutes, not days.',
  },
  {
    route: '/contacts',
    selector: '',
    title: 'The right people, in the loop',
    body: 'Tell us who should hear about hiring, billing, and safety — we route every notification to the right person automatically. That is the whole tour: your consultant does the work, and this portal shows you it is done.',
  },
]
